import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FinishProService } from '../../services/finish-pro.service';
import { Routes } from '../../models/routes';
import { LoadingProService } from '../../services/loading-pro.service';
import { ProfileService } from '../../../core/profile/profile.service';
import { LanguageService } from '../../services/language.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { AuthPreference } from '../../../api/model/authPreference';
import { AuthorizationModalComponent } from '../../components/modal/authorization-modal/authorization-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { IvCertGenericResponseListCircuitMembersCountPendingMsgsResponse } from '../../../api/model/ivCertGenericResponseListCircuitMembersCountPendingMsgsResponse';
import { CircuitService } from '../../../api/api/circuit.service';
import { CustomTextType, IntroTexts } from '../document-viewer/types/document-viewer-types';
import { TranslateDataService } from '../../../base/services/translate-data/translate-data.service';
import { CustomTextToSignatureContent } from '../../../api/model/customTextToSignatureContent';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-intro',
  templateUrl: './intro.component.html',
  styleUrls: ['./intro.component.scss'],
})

export class IntroComponent implements OnInit, AfterViewInit, OnDestroy {

  disableAcceptButton: boolean = false;

  showLanguageSelector: BehaviorSubject<boolean>;

  _transParam: boolean = false;

  authPreference: AuthPreference[] = [];

  isOperationAuthorized: boolean = false;

  operationNeedsAuthorization: boolean = false;

  showIntro: boolean = true;

  showChat: boolean = false;

  chatContent: any;

  chatSender: string;

  hasNotifications: boolean;

  texts: IntroTexts = {
    title: '',
    titleStep1: '',
    titleStep2: '',
    titleStep3: '',
    titleStep4: '',
    descriptionStep1: '',
    descriptionStep2: '',
    descriptionStep3: '',
    descriptionStep4: '',
  };

  isVisibleIntroText: boolean = true;

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _finishProService: FinishProService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _profile: ProfileService,
    private _dialog: MatDialog,
    private _loadingPro: LoadingProService,
    private _languageService: LanguageService,
    private _circuit: CircuitService,
    private _translateData: TranslateDataService
  ) { }

  ngOnInit(): void {
    this._languageService.subscribeToTranslationChange(this as Component);
  }

  async ngAfterViewInit(): Promise<void> {
    if (!this._finishProService.operationDetailResponse) {
      await this._finishProService.getOperationDetail(true);
    }

    this._finishProService.updateFirstAccess();
    this.showLanguageSelector = this._languageService.showLanguageSelector;
    this._loadingPro.setLoader(false);
    this._loadingPro.setNextOperationLoader(false);
    this.getTranslationsByOperationType();
    this.getCustomText();
  }

  ngOnDestroy(): void {
    this.destroy$.unsubscribe();
   }

  getTranslationsByOperationType(): void {
    const operationType = this._finishProService.operationDetailResponse?.operationType;
    const positionUsername = this._profile.getUser()?.name || '';
    const totalDocs = this._finishProService?.mainDocs?.length || 0;
    const nameUser = this._finishProService.operationDetailResponse?.nameUser || positionUsername;
    const userCreator = this._finishProService.operationDetailResponse?.userCreatorName || '';
    const operationName = this._finishProService.operationDetailResponse?.operacion || '';
    this.texts = this._translateData.getTranslationsIntro(operationType, totalDocs, nameUser, userCreator, operationName);
  }

  getCustomText(): void {
    const content: CustomTextToSignatureContent = this._finishProService.operationDetailResponse?.customTexts?.find(t => t.type === CustomTextType.PRESIGNVIEW)?.content;
    this.texts.intro = content?.text ? content.text.replace(/{/g, '').replace(/}/g, '') : '';
    this.isVisibleIntroText = content?.visible ?? true;
  }

  async onNavigate(): Promise<void> {
    this._finishProService.isProcessStarted = true;
    this.disableAcceptButton = true;

    this._loadingPro.setLoader(true);

    if (!this._finishProService.operationDetailResponse) {
      await this._finishProService.getOperationDetail(true);
    }

    this.verifyOperationAuthorization();
  }

  verifyOperationAuthorization(): void {
    this.authPreference = this._finishProService.authPreferenceResponse;
    if (this.authPreference?.length) {
      this.operationNeedsAuthorization = true;
      this.openAuthModal(this.authPreference);
    } else {
      this.navigate();
    }
  }

  openAuthModal(authPreference: AuthPreference[]): void {
    this._loadingPro.setLoader(false);
    this._finishProService.operationRequest.acceptedAuth = false;
    this.showIntro = false;
    const dialog = this._dialog.open(AuthorizationModalComponent, {
      width: '30rem',
      backdropClass: 'dialog__overlay',
      data: { authPreference },
      autoFocus: false,
      disableClose: true,
    });

    dialog.afterClosed().subscribe((response: { isOperationAuthorized: boolean }) => {
      if (response) {
        this.isOperationAuthorized = response.isOperationAuthorized;
        if (this.isOperationAuthorized) {
          if (this.operationNeedsAuthorization && !this.isOperationAuthorized) {
            this.openAuthModal(this.authPreference);
            return;
          }
          this._finishProService.operationRequest.acceptedAuth = true;
          this.navigate();
        } else {
          this.disableAcceptButton = false;
          this.showIntro = true;
        }
      }
    });
  }

  async navigate(): Promise<void> {
    try {
      await this._finishProService.getOperationDocumentsList(true);
      const route = this._finishProService.getRoutePath();
      this._router.navigate([route], { relativeTo: this._route.parent });
    } catch (e) {
      this.showIntro = true;
      this.disableAcceptButton = false;
      console.log(e);
    }
  }

  exitProcess(): void {
    this._languageService.resetCurrentLanguage();
    this._router.navigate([Routes.DASHBOARD]);
  }

  showChatButton(event: boolean): void {
    const token = this._profile.getToken();
    const signerGuid = sessionStorage.getItem('signer_guid');
    const userGuid = this._profile.getUserProperty('username');
    const email = this._finishProService.operationDetailResponse?.mailUser;
    const idCircuit = this._finishProService.operationRequest.idCircuit;

    if (signerGuid) {
      this.chatSender = signerGuid;
      sessionStorage.removeItem('signer_guid');
    } else if (token) {
      this.chatSender = userGuid;
    } else {
      this.chatSender = email;
    }

    this.chatContent = [];
    this._circuit.circuitGetReceiversCountPendingMessages(idCircuit, token || '1', this.chatSender)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: IvCertGenericResponseListCircuitMembersCountPendingMsgsResponse) => {
          this.chatContent = response.data;
          this.hasNotifications = this.chatContent.some(item => item.pending > 0);
          this.showChat = event;
        },
        (error) => {
          console.log(error)
        }
      );
  }
}
